import React, { useRef, useState } from "react";
import { Button, Divider, Dropdown, Input, Popover, Tooltip, Typography } from "antd";
import {
  BoldOutlined,
  CodeOutlined,
  ItalicOutlined,
  PlusOutlined,
  SmileOutlined,
  StrikethroughOutlined,
} from "@ant-design/icons";
import EmojiPicker from "emoji-picker-react";

const { Text } = Typography;

/**
 * The body box from WhatsApp Manager's template editor: a plain textarea
 * whose toolbar writes WhatsApp's own markers (*bold*, _italic_,
 * ~strikethrough~, ```monospace```), inserts emoji at the caret, and adds
 * variables. The value stays plain text — exactly what WhatsApp receives.
 *
 * Works as an antd Form control (`value` / `onChange(string)`).
 *
 * `variables` is what "Add variable" offers: a list of strings to insert,
 * e.g. ["{{ first_name }}", …] for a manual template. `onAddVariable`, when
 * given instead, is called to produce the next one (Meta's {{1}}, {{2}} …).
 */

const MARKERS = [
  { key: "bold", marker: "*", icon: <BoldOutlined />, label: "Bold" },
  { key: "italic", marker: "_", icon: <ItalicOutlined />, label: "Italic" },
  { key: "strike", marker: "~", icon: <StrikethroughOutlined />, label: "Strikethrough" },
  { key: "mono", marker: "```", icon: <CodeOutlined />, label: "Monospace" },
];

const WhatsAppTextEditor = ({
  value = "",
  onChange,
  rows = 5,
  maxLength,
  placeholder,
  variables,
  onAddVariable,
  singleLine = false,
  disabled = false,
}) => {
  const ref = useRef(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const text = value || "";

  const element = () =>
    singleLine ? ref.current?.input : ref.current?.resizableTextArea?.textArea;

  const selection = () => {
    const el = element();
    return el ? [el.selectionStart, el.selectionEnd] : [text.length, text.length];
  };

  const restore = (start, end) =>
    requestAnimationFrame(() => {
      const el = element();
      if (!el) return;
      el.focus();
      el.setSelectionRange(start, end);
    });

  const insert = (insertText) => {
    const [start, end] = selection();
    onChange?.(`${text.slice(0, start)}${insertText}${text.slice(end)}`);
    restore(start + insertText.length, start + insertText.length);
  };

  // Like WhatsApp Manager: wrap the selection, or drop an empty pair with
  // the caret between; clicking again on a wrapped selection unwraps it.
  const wrap = (marker) => {
    const [start, end] = selection();
    const selected = text.slice(start, end);
    const size = marker.length;
    const wrapped =
      text.slice(start - size, start) === marker && text.slice(end, end + size) === marker;
    if (selected && wrapped) {
      onChange?.(`${text.slice(0, start - size)}${selected}${text.slice(end + size)}`);
      restore(start - size, end - size);
      return;
    }
    // Markers must hug the text, so keep surrounding spaces outside them.
    const lead = selected.match(/^\s*/)[0];
    const trail = selected.match(/\s*$/)[0];
    const core = selected.trim();
    const next = `${lead}${marker}${core}${marker}${trail}`;
    onChange?.(`${text.slice(0, start)}${next}${text.slice(end)}`);
    const from = start + lead.length + size;
    restore(from, from + core.length);
  };

  const variableItems = (variables || []).map((variable) => ({
    key: variable,
    label: variable,
  }));

  const addVariableButton = onAddVariable ? (
    <Button size="small" type="text" icon={<PlusOutlined />} disabled={disabled}
      onClick={() => insert(onAddVariable())}>
      Add variable
    </Button>
  ) : variableItems.length ? (
    <Dropdown
      trigger={["click"]}
      disabled={disabled}
      menu={{
        items: variableItems,
        onClick: ({ key }) => insert(key),
        style: { maxHeight: 280, overflowY: "auto" },
      }}
    >
      <Button size="small" type="text" icon={<PlusOutlined />}>
        Add variable
      </Button>
    </Dropdown>
  ) : null;

  const field = singleLine ? (
    <Input
      ref={ref}
      value={text}
      disabled={disabled}
      maxLength={maxLength}
      placeholder={placeholder}
      variant="borderless"
      onChange={(event) => onChange?.(event.target.value)}
    />
  ) : (
    <Input.TextArea
      ref={ref}
      value={text}
      rows={rows}
      disabled={disabled}
      maxLength={maxLength}
      placeholder={placeholder}
      variant="borderless"
      style={{ resize: "vertical" }}
      onChange={(event) => onChange?.(event.target.value)}
    />
  );

  return (
    <div
      style={{
        border: "1px solid #d9d9d9",
        borderRadius: 6,
        background: disabled ? "#f5f5f5" : "#fff",
      }}
    >
      {field}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 4,
          padding: "2px 6px",
          borderTop: "1px solid #f0f0f0",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Popover
            open={emojiOpen}
            onOpenChange={setEmojiOpen}
            trigger="click"
            placement="bottomLeft"
            content={
              // Mounted only while open (a heavy picker); the wrapper must
              // always exist, or antd never opens the Popover.
              <div style={{ width: 320, height: 360 }}>
                {emojiOpen ? (
                  <EmojiPicker
                    onEmojiClick={(emoji) => insert(emoji.emoji)}
                    height={360}
                    width={320}
                  />
                ) : null}
              </div>
            }
          >
            <Tooltip title="Emoji">
              <Button size="small" type="text" icon={<SmileOutlined />} disabled={disabled}
                aria-label="Emoji" />
            </Tooltip>
          </Popover>
          {MARKERS.map(({ key, marker, icon, label }) => (
            <Tooltip key={key} title={`${label}  ${marker}text${marker}`}>
              <Button
                size="small"
                type="text"
                icon={icon}
                aria-label={label}
                disabled={disabled}
                // Keep the textarea's selection when the button takes focus.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => wrap(marker)}
              />
            </Tooltip>
          ))}
          {addVariableButton && <Divider type="vertical" style={{ margin: "0 4px" }} />}
          {addVariableButton}
        </div>
        {maxLength ? (
          <Text type={text.length > maxLength * 0.9 ? "warning" : "secondary"} style={{ fontSize: 12 }}>
            {text.length}/{maxLength}
          </Text>
        ) : null}
      </div>
    </div>
  );
};

export default WhatsAppTextEditor;
